import type { CharacterId } from "./core/types";

export const WARDROBE_UNLOCK_LEVELS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10] as const;
export type WardrobeUnlockLevel = (typeof WARDROBE_UNLOCK_LEVELS)[number];

export const ACCESSORY_IDS = [
  "dragon-adventurer-hat",
  "dragon-fire-scarf",
  "dragon-ember-cape",
  "dragon-crystal-glasses",
  "dragon-knight-helmet",
  "dragon-golden-wing-guards",
  "dragon-volcano-crown",
  "dragon-hero-medal",
  "dragon-royal-cape",
  "dragon-star-crown",
  "unicorn-flower-crown",
  "unicorn-rainbow-scarf",
  "unicorn-heart-glasses",
  "unicorn-star-hat",
  "unicorn-butterfly-wings",
  "unicorn-cloud-cape",
  "unicorn-moon-tiara",
  "unicorn-rainbow-wings",
  "unicorn-princess-cape",
  "unicorn-magic-crown",
] as const;

export type AccessoryId = (typeof ACCESSORY_IDS)[number];

export const ACCESSORY_CATEGORIES = [
  "hat",
  "scarf",
  "cape",
  "glasses",
  "helmet",
  "wings",
  "crown",
  "medal",
  "tiara",
] as const;

export type AccessoryCategory = (typeof ACCESSORY_CATEGORIES)[number];
export type AccessoryLayer = "back" | "front";
export type AccessoryPoint = readonly [x: number, y: number];
export type AccessoryAnchor = "head" | "eyes" | "neck" | "back" | "chest" | "wing";

interface AccessoryShapeBase {
  readonly layer: AccessoryLayer;
  readonly fill?: number;
  readonly stroke?: number;
  readonly strokeWidth?: number;
  readonly opacity?: number;
}

export interface AccessoryCircle extends AccessoryShapeBase {
  readonly kind: "circle";
  readonly cx: number;
  readonly cy: number;
  readonly radius: number;
}

export interface AccessoryEllipse extends AccessoryShapeBase {
  readonly kind: "ellipse";
  readonly cx: number;
  readonly cy: number;
  readonly radiusX: number;
  readonly radiusY: number;
}

export interface AccessoryRoundedRect extends AccessoryShapeBase {
  readonly kind: "rounded-rect";
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
  readonly radius: number;
}

export interface AccessoryPolygon extends AccessoryShapeBase {
  readonly kind: "polygon";
  readonly points: readonly AccessoryPoint[];
}

export type AccessoryPathCommand =
  | { readonly kind: "move"; readonly x: number; readonly y: number }
  | { readonly kind: "line"; readonly x: number; readonly y: number }
  | { readonly kind: "quadratic"; readonly controlX: number; readonly controlY: number; readonly x: number; readonly y: number }
  | {
      readonly kind: "cubic";
      readonly control1X: number;
      readonly control1Y: number;
      readonly control2X: number;
      readonly control2Y: number;
      readonly x: number;
      readonly y: number;
    }
  | { readonly kind: "close" };

/** Organic curves are shared by SVG previews and Phaser through sampled geometry. */
export interface AccessoryPath extends AccessoryShapeBase {
  readonly kind: "path";
  readonly commands: readonly AccessoryPathCommand[];
  readonly closed: boolean;
}

export type AccessoryShape =
  | AccessoryCircle
  | AccessoryEllipse
  | AccessoryRoundedRect
  | AccessoryPolygon
  | AccessoryPath;

export interface AccessoryDefinition {
  readonly id: AccessoryId;
  readonly characterId: CharacterId;
  readonly displayName: string;
  readonly category: AccessoryCategory;
  readonly categoryDisplayName: string;
  readonly unlockAfterLevel: WardrobeUnlockLevel;
  /** Full-character transparent skin used by React and Phaser. */
  readonly outfitAssetHref: string;
  /** Stable Phaser cache key; changing this would break preload/runtime lookup. */
  readonly outfitTextureKey: string;
  /** Shapes are authored around a character-specific anatomical anchor. */
  readonly anchor: AccessoryAnchor;
  readonly shapes: readonly AccessoryShape[];
}

export interface AccessoryAnchorDefinition {
  readonly x: number;
  readonly y: number;
}

export interface CharacterPreviewDefinition {
  readonly displayName: string;
  readonly assetHref: string;
  readonly width: number;
  readonly height: number;
  readonly outfitWidth: number;
  readonly outfitHeight: number;
}

export const ACCESSORY_VIEW_BOX = {
  x: -82,
  y: -86,
  width: 164,
  height: 164,
} as const;

/**
 * Stable anatomy rigs for the right-facing character art. Keeping placement
 * separate from item geometry prevents hats, eyewear, capes and wings from
 * drifting when an item is redesigned.
 */
export const CHARACTER_ACCESSORY_ANCHORS: Readonly<
  Record<CharacterId, Readonly<Record<AccessoryAnchor, AccessoryAnchorDefinition>>>
> = {
  dragon: {
    head: { x: 38, y: -50 },
    eyes: { x: 45, y: -29 },
    neck: { x: 8, y: -7 },
    back: { x: -25, y: -15 },
    chest: { x: 13, y: 5 },
    wing: { x: -32, y: -24 },
  },
  unicorn: {
    head: { x: 23, y: -52 },
    eyes: { x: 43, y: -28 },
    neck: { x: 9, y: -3 },
    back: { x: -25, y: -14 },
    chest: { x: 12, y: 5 },
    wing: { x: -27, y: -15 },
  },
};

export const CHARACTER_PREVIEW_DEFINITIONS: Readonly<Record<CharacterId, CharacterPreviewDefinition>> = {
  dragon: {
    displayName: "הדרקון",
    assetHref: "/assets/dragon.webp",
    width: 128,
    height: 108,
    outfitWidth: 164,
    outfitHeight: 136.67,
  },
  unicorn: {
    displayName: "חד־הקרן",
    assetHref: "/assets/unicorn.webp",
    width: 136,
    height: 120,
    outfitWidth: 164,
    outfitHeight: 136.67,
  },
};

export const ACCESSORY_CATEGORY_DISPLAY_NAMES: Readonly<Record<AccessoryCategory, string>> = {
  hat: "כובע",
  scarf: "צעיף",
  cape: "גלימה",
  glasses: "משקפיים",
  helmet: "קסדה",
  wings: "כנפיים",
  crown: "כתר",
  medal: "מדליה",
  tiara: "נזר",
};

const NAVY = 0x17154a;
const GOLD = 0xf5bd45;
const GOLD_LIGHT = 0xffe7a0;
const RUBY = 0xef415d;
const TEAL = 0x21c9cf;
const LAVENDER = 0x9d63e8;
const SKY = 0x73d7ff;
const IVORY = 0xfff8e7;
const ORANGE = 0xf47c3c;
const PINK = 0xff78ba;
const WHITE = 0xffffff;
const INK = 0x24143f;
const LEATHER_DARK = 0x6f351d;
const LEATHER = 0xa85a2a;
const LEATHER_LIGHT = 0xd98a45;
const BRONZE = 0xb65a24;
const OBSIDIAN = 0x35213f;
const LAVA = 0xff5b28;
const SILVER_DARK = 0x68748e;
const SILVER = 0xbcc7d9;
const SILVER_LIGHT = 0xf2f6ff;
const PURPLE_DARK = 0x4b277f;
const PURPLE = 0x8150cf;
const MAGENTA = 0xd93d91;
const ROSE = 0xff9acb;
const MINT = 0x63e0c2;
const BLUE = 0x40aee8;

type ShapeOptions = Pick<AccessoryShapeBase, "stroke" | "strokeWidth" | "opacity">;
type PathOptions = ShapeOptions & { readonly closed?: boolean };

function circle(
  layer: AccessoryLayer,
  cx: number,
  cy: number,
  radius: number,
  fill: number,
  options: ShapeOptions = {},
): AccessoryCircle {
  return { kind: "circle", layer, cx, cy, radius, fill, ...options };
}

function ellipse(
  layer: AccessoryLayer,
  cx: number,
  cy: number,
  radiusX: number,
  radiusY: number,
  fill: number,
  options: ShapeOptions = {},
): AccessoryEllipse {
  return { kind: "ellipse", layer, cx, cy, radiusX, radiusY, fill, ...options };
}

function roundedRect(
  layer: AccessoryLayer,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number,
  fill: number,
  options: ShapeOptions = {},
): AccessoryRoundedRect {
  return { kind: "rounded-rect", layer, x, y, width, height, radius, fill, ...options };
}

function polygon(
  layer: AccessoryLayer,
  points: readonly AccessoryPoint[],
  fill: number,
  options: ShapeOptions = {},
): AccessoryPolygon {
  return { kind: "polygon", layer, points, fill, ...options };
}

const move = (x: number, y: number): AccessoryPathCommand => ({ kind: "move", x, y });
const line = (x: number, y: number): AccessoryPathCommand => ({ kind: "line", x, y });
const quadratic = (
  controlX: number,
  controlY: number,
  x: number,
  y: number,
): AccessoryPathCommand => ({ kind: "quadratic", controlX, controlY, x, y });
const cubic = (
  control1X: number,
  control1Y: number,
  control2X: number,
  control2Y: number,
  x: number,
  y: number,
): AccessoryPathCommand => ({
  kind: "cubic",
  control1X,
  control1Y,
  control2X,
  control2Y,
  x,
  y,
});
const close = (): AccessoryPathCommand => ({ kind: "close" });

function path(
  layer: AccessoryLayer,
  commands: readonly AccessoryPathCommand[],
  fill: number | undefined,
  options: PathOptions = {},
): AccessoryPath {
  const { closed = commands.some((command) => command.kind === "close"), ...shapeOptions } = options;
  return { kind: "path", layer, commands, closed, fill, ...shapeOptions };
}

function detailPath(
  layer: AccessoryLayer,
  commands: readonly AccessoryPathCommand[],
  stroke: number,
  strokeWidth = 1.4,
  opacity = 1,
): AccessoryPath {
  return path(layer, commands, undefined, { stroke, strokeWidth, opacity, closed: false });
}

function star(
  layer: AccessoryLayer,
  cx: number,
  cy: number,
  outerRadius: number,
  innerRadius: number,
  fill: number,
  options: ShapeOptions = {},
): AccessoryPolygon {
  const points: AccessoryPoint[] = [];
  for (let index = 0; index < 10; index += 1) {
    const angle = -Math.PI / 2 + (Math.PI * index) / 5;
    const radius = index % 2 === 0 ? outerRadius : innerRadius;
    points.push([cx + Math.cos(angle) * radius, cy + Math.sin(angle) * radius]);
  }
  return polygon(layer, points, fill, options);
}

function flower(
  cx: number,
  cy: number,
  petalColor: number,
  centerColor: number,
  radius = 4.6,
): readonly AccessoryShape[] {
  return [
    circle("front", cx - radius * 0.7, cy, radius, petalColor, { stroke: INK, strokeWidth: 1.15 }),
    circle("front", cx + radius * 0.7, cy, radius, petalColor, { stroke: INK, strokeWidth: 1.15 }),
    circle("front", cx, cy - radius * 0.7, radius, petalColor, { stroke: INK, strokeWidth: 1.15 }),
    circle("front", cx, cy + radius * 0.7, radius, petalColor, { stroke: INK, strokeWidth: 1.15 }),
    circle("front", cx, cy, radius * 0.48, centerColor, { stroke: INK, strokeWidth: 0.8 }),
    circle("front", cx - 1.1, cy - 1.2, radius * 0.18, WHITE, { opacity: 0.85 }),
  ];
}

function accessory(
  id: AccessoryId,
  characterId: CharacterId,
  displayName: string,
  category: AccessoryCategory,
  unlockAfterLevel: WardrobeUnlockLevel,
  anchor: AccessoryAnchor,
  shapes: readonly AccessoryShape[],
): AccessoryDefinition {
  return {
    id,
    characterId,
    displayName,
    category,
    categoryDisplayName: ACCESSORY_CATEGORY_DISPLAY_NAMES[category],
    unlockAfterLevel,
    outfitAssetHref: `/assets/wardrobe/${id}.webp`,
    outfitTextureKey: `wardrobe-${id}`,
    anchor,
    shapes,
  };
}

export const WARDROBE_ACCESSORIES: readonly AccessoryDefinition[] = [
  accessory("dragon-adventurer-hat", "dragon", "כובע ההרפתקן", "hat", 1, "head", [
    path("front", [
      move(-19, 2), quadratic(-20, -14, -8, -20), quadratic(8, -27, 21, -15),
      quadratic(24, -7, 24, 3), close(),
    ], LEATHER, { stroke: INK, strokeWidth: 2 }),
    path("front", [
      move(-13, -2), quadratic(-9, -15, 2, -19), quadratic(12, -20, 18, -12),
      quadratic(5, -15, -13, -2), close(),
    ], LEATHER_LIGHT, { opacity: 0.72 }),
    path("front", [
      move(-25, 2), quadratic(-3, -1, 29, 3), quadratic(31, 6, 26, 8),
      quadratic(-2, 6, -28, 6), quadratic(-30, 4, -25, 2), close(),
    ], LEATHER_DARK, { stroke: INK, strokeWidth: 2 }),
    roundedRect("front", -18, -5, 41, 6, 2.5, BRONZE, { stroke: INK, strokeWidth: 1.2 }),
    roundedRect("front", -4, -6, 9, 8, 2, GOLD, { stroke: INK, strokeWidth: 1 }),
    circle("front", 10, -11, 8, GOLD, { stroke: INK, strokeWidth: 1.5 }),
    circle("front", 10, -11, 5.5, TEAL, { stroke: 0x0b7285, strokeWidth: 1 }),
    ellipse("front", 8.5, -13, 1.8, 2.7, WHITE, { opacity: 0.9 }),
    detailPath("front", [move(-12, -15), quadratic(-2, -20, 7, -18)], GOLD_LIGHT, 1.2, 0.85),
  ]),
  accessory("dragon-fire-scarf", "dragon", "צעיף האש", "scarf", 2, "neck", [
    path("back", [
      move(1, -4), cubic(-17, -5, -35, 3, -61, -7), quadratic(-51, 4, -61, 10),
      quadratic(-43, 10, -31, 17), quadratic(-45, 21, -56, 29),
      quadratic(-30, 28, -8, 17), line(8, 5), close(),
    ], RUBY, { stroke: INK, strokeWidth: 2 }),
    path("back", [
      move(-9, 5), cubic(-28, 5, -39, 11, -50, 8), quadratic(-40, 15, -48, 20),
      quadratic(-25, 19, -7, 12), close(),
    ], ORANGE, { opacity: 0.95 }),
    path("back", [
      move(-28, 12), quadratic(-39, 15, -44, 20), quadratic(-31, 18, -18, 15), close(),
    ], GOLD_LIGHT),
    path("front", [
      move(-5, -8), cubic(8, -15, 28, -11, 35, 0), quadratic(31, 13, 17, 16),
      quadratic(1, 14, -7, 4), close(),
    ], RUBY, { stroke: INK, strokeWidth: 2 }),
    path("front", [
      move(-2, -4), quadratic(14, -9, 29, 1), quadratic(22, 7, 6, 8), close(),
    ], ORANGE),
    detailPath("front", [move(0, 10), quadratic(14, 14, 27, 7)], GOLD_LIGHT, 2),
    polygon("front", [[27, -3], [33, 2], [27, 9], [21, 3]], GOLD, { stroke: INK, strokeWidth: 1.2 }),
    circle("front", 27, 3, 2.3, RUBY),
  ]),
  accessory("dragon-ember-cape", "dragon", "גלימת הגחלת", "cape", 3, "neck", [
    path("back", [
      move(1, -6), cubic(-19, -8, -48, -2, -66, 10), cubic(-58, 24, -64, 40, -56, 51),
      quadratic(-44, 43, -34, 52), quadratic(-23, 42, -8, 49),
      quadratic(-1, 27, 14, 6), close(),
    ], 0x7f1732, { stroke: INK, strokeWidth: 2.4 }),
    path("back", [
      move(-3, -1), cubic(-24, -2, -46, 4, -58, 12), quadratic(-48, 24, -53, 41),
      quadratic(-39, 34, -31, 45), quadratic(-20, 34, -8, 42), quadratic(-3, 18, 10, 5), close(),
    ], RUBY),
    path("back", [
      move(-14, 4), cubic(-29, 6, -43, 10, -51, 16), quadratic(-39, 22, -44, 31),
      quadratic(-29, 26, -21, 36), quadratic(-13, 24, -3, 31), line(5, 9), close(),
    ], ORANGE, { opacity: 0.92 }),
    detailPath("back", [
      move(-59, 47), quadratic(-49, 36, -39, 43), quadratic(-31, 32, -22, 42),
      quadratic(-13, 31, -7, 45),
    ], GOLD, 2.2),
    detailPath("back", [move(-37, 5), quadratic(-25, 20, -26, 39)], GOLD_LIGHT, 1.3, 0.85),
    path("front", [
      move(-4, -9), quadratic(8, -16, 23, -7), quadratic(17, 6, 4, 10),
      quadratic(-6, 6, -4, -9), close(),
    ], ORANGE, { stroke: INK, strokeWidth: 1.8 }),
    circle("front", 14, -3, 7, GOLD, { stroke: INK, strokeWidth: 1.5 }),
    star("front", 14, -3, 4.8, 2.1, RUBY),
    circle("front", 12.4, -5, 1.3, WHITE, { opacity: 0.8 }),
  ]),
  accessory("dragon-crystal-glasses", "dragon", "משקפי הקריסטל", "glasses", 4, "eyes", [
    detailPath("front", [move(-28, -4), quadratic(-23, -10, -18, -8)], LEATHER_DARK, 3),
    circle("front", -10, -1, 12, GOLD, { stroke: INK, strokeWidth: 2 }),
    circle("front", -10, -1, 8.5, SKY, { stroke: 0x176fa3, strokeWidth: 1.2, opacity: 0.88 }),
    path("front", [move(-16, -8), line(-11, -8), line(-3, 2), line(-7, 6), close()], WHITE, { opacity: 0.58 }),
    circle("front", 14, 0, 10.5, GOLD, { stroke: INK, strokeWidth: 2 }),
    circle("front", 14, 0, 7.2, BLUE, { stroke: 0x176fa3, strokeWidth: 1.1, opacity: 0.86 }),
    path("front", [move(9, -6), line(13, -6), line(19, 1), line(16, 4), close()], WHITE, { opacity: 0.58 }),
    roundedRect("front", 0, -3, 5, 3.5, 1.5, BRONZE, { stroke: INK, strokeWidth: 1 }),
    roundedRect("front", -15, -13, 9, 3, 1.5, GOLD_LIGHT, { opacity: 0.9 }),
    roundedRect("front", 10, -11, 8, 2.6, 1.3, GOLD_LIGHT, { opacity: 0.9 }),
  ]),
  accessory("dragon-knight-helmet", "dragon", "קסדת האביר", "helmet", 5, "head", [
    path("back", [
      move(-25, 6), line(-24, -12), quadratic(-18, -29, 1, -30),
      quadratic(22, -28, 28, -8), line(27, 14), line(18, 22), line(-19, 18), close(),
    ], SILVER_DARK, { stroke: INK, strokeWidth: 2.3 }),
    path("front", [
      move(-21, 4), line(-20, -10), quadratic(-12, -26, 2, -26),
      quadratic(18, -24, 24, -8), line(22, 5), quadratic(8, -3, -21, 4), close(),
    ], SILVER, { stroke: INK, strokeWidth: 1.7 }),
    path("front", [
      move(-13, -14), quadratic(0, -25, 15, -14), quadratic(2, -18, -13, -8), close(),
    ], SILVER_LIGHT, { opacity: 0.75 }),
    path("front", [
      move(-24, 2), quadratic(2, -7, 28, 1), line(26, 9), quadratic(3, 2, -22, 10), close(),
    ], 0x8290a8, { stroke: INK, strokeWidth: 1.7 }),
    detailPath("front", [move(-15, 3), line(-6, 1), move(1, 0), line(10, 0), move(16, 1), line(23, 3)], INK, 1.7),
    path("front", [move(17, 7), line(27, 8), line(24, 25), line(15, 19), close()], SILVER_DARK, { stroke: INK, strokeWidth: 1.8 }),
    path("front", [move(18, 9), line(23, 10), line(21, 18), line(17, 16), close()], SILVER_LIGHT, { opacity: 0.65 }),
    circle("front", -17, 10, 2.2, GOLD, { stroke: INK, strokeWidth: 0.8 }),
    circle("front", 20, -7, 2.2, TEAL, { stroke: INK, strokeWidth: 0.8 }),
  ]),
  accessory("dragon-golden-wing-guards", "dragon", "מגיני כנפי הזהב", "wings", 6, "wing", [
    path("front", [
      move(31, 20), quadratic(11, 3, -7, -13), quadratic(-23, -27, -39, -25),
      quadratic(-30, -8, -35, 5), quadratic(-18, 0, -14, 13),
      quadratic(-2, 5, 7, 20), quadratic(17, 15, 31, 20), close(),
    ], BRONZE, { stroke: INK, strokeWidth: 2.2 }),
    path("front", [
      move(27, 16), quadratic(8, 0, -7, -10), quadratic(-21, -21, -31, -21),
      quadratic(-23, -8, -28, 0), quadratic(-14, -3, -10, 8),
      quadratic(1, 1, 9, 15), close(),
    ], GOLD, { stroke: 0x9c4f1f, strokeWidth: 1.4 }),
    path("front", [
      move(18, 11), quadratic(5, -1, -7, -8), quadratic(-16, -14, -23, -15),
      quadratic(-16, -6, -20, -1), quadratic(-7, -1, -4, 7), quadratic(6, 3, 18, 11), close(),
    ], GOLD_LIGHT, { opacity: 0.86 }),
    detailPath("front", [move(27, 17), quadratic(5, 5, -31, -21)], WHITE, 1.2, 0.65),
    detailPath("front", [move(2, 5), quadratic(-4, -3, -11, -10)], BRONZE, 1.6),
    detailPath("front", [move(-9, 1), quadratic(-16, -6, -23, -10)], BRONZE, 1.6),
    path("front", [move(20, 14), quadratic(29, 12, 34, 20), line(27, 28), line(17, 21), close()], GOLD, { stroke: INK, strokeWidth: 1.7 }),
    circle("front", 25, 19, 3.5, RUBY, { stroke: INK, strokeWidth: 1 }),
  ]),
  accessory("dragon-volcano-crown", "dragon", "כתר הר הגעש", "crown", 7, "head", [
    path("back", [
      move(-26, 7), line(-23, -10), line(-15, -3), line(-10, -27), line(-2, -17),
      line(5, -32), line(12, -17), line(20, -27), line(25, -4), line(30, -11), line(27, 9), close(),
    ], LAVA, { opacity: 0.42 }),
    path("front", [
      move(-24, 5), line(-20, -8), line(-13, -2), line(-8, -24), line(0, -15),
      line(6, -29), line(12, -14), line(19, -23), line(23, -2), line(28, -8), line(25, 7), close(),
    ], OBSIDIAN, { stroke: INK, strokeWidth: 2 }),
    path("front", [move(-8, -20), line(-1, -12), line(5, -25), line(10, -11), line(18, -19), line(20, -5), line(12, -9), line(6, 0), line(-1, -8), line(-12, -2), close()], 0x623046, { opacity: 0.95 }),
    detailPath("front", [move(-18, -5), line(-10, -8), line(-3, 1), line(4, -10), line(11, -3), line(18, -13), line(22, -2)], LAVA, 2.4),
    detailPath("front", [move(-7, -22), line(-3, -14), line(2, -17)], GOLD, 1.4),
    detailPath("front", [move(12, -15), line(16, -10), line(14, -4)], GOLD, 1.3),
    roundedRect("front", -25, 3, 52, 9, 3.5, BRONZE, { stroke: INK, strokeWidth: 1.6 }),
    roundedRect("front", -20, 4.5, 42, 3, 1.5, GOLD, { opacity: 0.85 }),
    polygon("front", [[1, 2], [7, 7], [1, 13], [-5, 7]], RUBY, { stroke: INK, strokeWidth: 1 }),
    circle("front", 0, 5.4, 1.4, WHITE, { opacity: 0.75 }),
  ]),
  accessory("dragon-hero-medal", "dragon", "מדליית הגיבור", "medal", 8, "chest", [
    path("front", [move(-14, -28), line(-2, -20), line(0, -1), line(-10, 2), close()], TEAL, { stroke: INK, strokeWidth: 1.6 }),
    path("front", [move(14, -28), line(2, -20), line(0, -1), line(10, 2), close()], PURPLE, { stroke: INK, strokeWidth: 1.6 }),
    path("front", [move(-8, -23), line(0, -16), line(8, -23), line(5, -5), line(-5, -5), close()], GOLD_LIGHT, { opacity: 0.9 }),
    star("front", 0, 11, 17, 13, BRONZE, { stroke: INK, strokeWidth: 1.8 }),
    circle("front", 0, 11, 13, GOLD, { stroke: INK, strokeWidth: 1.6 }),
    circle("front", 0, 11, 9.2, RUBY, { stroke: 0xa72844, strokeWidth: 1 }),
    star("front", 0, 11, 7.2, 3.2, GOLD_LIGHT, { stroke: INK, strokeWidth: 0.9 }),
    circle("front", -3, 7, 2, WHITE, { opacity: 0.75 }),
    detailPath("front", [move(-8, 16), quadratic(0, 21, 8, 16)], ORANGE, 1.2),
  ]),
  accessory("dragon-royal-cape", "dragon", "גלימת המלכות", "cape", 9, "neck", [
    path("back", [
      move(2, -8), cubic(-22, -9, -51, -2, -68, 10), cubic(-59, 27, -68, 43, -57, 55),
      quadratic(-44, 46, -31, 54), quadratic(-18, 43, -4, 49), quadratic(2, 23, 15, 5), close(),
    ], PURPLE_DARK, { stroke: INK, strokeWidth: 2.5 }),
    path("back", [
      move(-2, -3), cubic(-23, -4, -47, 2, -60, 12), quadratic(-50, 27, -57, 44),
      quadratic(-42, 36, -31, 47), quadratic(-18, 36, -8, 43), quadratic(-2, 19, 10, 4), close(),
    ], PURPLE),
    path("back", [
      move(-8, 1), cubic(-25, 2, -39, 7, -49, 14), quadratic(-39, 26, -45, 37),
      quadratic(-31, 31, -23, 39), quadratic(-16, 24, -2, 31), line(5, 7), close(),
    ], 0xa06be7, { opacity: 0.72 }),
    detailPath("back", [move(-60, 49), quadratic(-48, 39, -37, 47), quadratic(-27, 37, -16, 45), quadratic(-10, 38, -6, 43)], GOLD, 2.2),
    star("back", -35, 20, 3.2, 1.4, GOLD_LIGHT),
    star("back", -18, 31, 2.5, 1.1, GOLD_LIGHT),
    star("back", -48, 34, 2.2, 1, GOLD_LIGHT),
    circle("front", -1, -5, 10, IVORY, { stroke: INK, strokeWidth: 1.4 }),
    circle("front", 8, -4, 10, IVORY, { stroke: INK, strokeWidth: 1.4 }),
    circle("front", 17, -1, 8, IVORY, { stroke: INK, strokeWidth: 1.4 }),
    circle("front", 9, -1, 6, GOLD, { stroke: INK, strokeWidth: 1.2 }),
    star("front", 9, -1, 4.2, 1.8, RUBY),
  ]),
  accessory("dragon-star-crown", "dragon", "כתר כוכבי הקסם", "crown", 10, "head", [
    path("back", [
      move(-28, 8), line(-24, -10), line(-17, -3), line(-12, -28), line(-3, -17),
      line(4, -34), line(11, -16), line(20, -29), line(22, -7), line(30, -13), line(27, 10), close(),
    ], ORANGE, { opacity: 0.5 }),
    path("front", [
      move(-26, 6), line(-21, -8), line(-14, -1), line(-9, -25), line(-1, -14),
      line(5, -31), line(12, -13), line(19, -26), line(21, -5), line(27, -10), line(25, 8), close(),
    ], GOLD, { stroke: INK, strokeWidth: 2 }),
    path("front", [
      move(-18, 2), line(-13, -14), line(-5, -7), line(5, -25), line(12, -6),
      line(18, -16), line(18, 3), close(),
    ], GOLD_LIGHT, { opacity: 0.82 }),
    detailPath("front", [move(-15, -1), line(-9, -17), move(1, -11), line(5, -25), move(14, -5), line(18, -19)], WHITE, 1.2, 0.7),
    roundedRect("front", -26, 4, 52, 10, 4, BRONZE, { stroke: INK, strokeWidth: 1.7 }),
    roundedRect("front", -21, 5.5, 42, 3.5, 1.7, GOLD_LIGHT, { opacity: 0.8 }),
    star("front", 3, 5, 9.5, 4.2, WHITE, { stroke: RUBY, strokeWidth: 1.5 }),
    circle("front", 3, 5, 3.2, SKY, { stroke: INK, strokeWidth: 0.8 }),
    circle("front", -14, 8.5, 3.1, RUBY, { stroke: INK, strokeWidth: 0.8 }),
    circle("front", 19, 8.5, 3.1, TEAL, { stroke: INK, strokeWidth: 0.8 }),
    circle("front", 1.7, 3.7, 1.1, WHITE, { opacity: 0.9 }),
  ]),

  accessory("unicorn-flower-crown", "unicorn", "זר פרחי הקסם", "crown", 1, "head", [
    detailPath("back", [move(-29, 3), cubic(-14, -7, 7, -7, 27, 2)], 0x238f68, 3),
    path("front", [move(-25, -1), quadratic(-20, -10, -13, -5), quadratic(-19, 1, -25, -1), close()], MINT, { stroke: INK, strokeWidth: 1 }),
    path("front", [move(10, -5), quadratic(18, -12, 24, -5), quadratic(16, 1, 10, -5), close()], MINT, { stroke: INK, strokeWidth: 1 }),
    path("front", [move(-3, 1), quadratic(2, 8, 9, 3), quadratic(3, -2, -3, 1), close()], 0x44b57f, { stroke: INK, strokeWidth: 0.9 }),
    ...flower(-14, -3, ROSE, GOLD, 4.8),
    ...flower(0, -6, LAVENDER, GOLD, 5.2),
    ...flower(15, -3, SKY, GOLD, 4.8),
    circle("front", 24, 0, 2.2, PINK, { stroke: INK, strokeWidth: 0.7 }),
    circle("front", -25, 2, 2, LAVENDER, { stroke: INK, strokeWidth: 0.7 }),
  ]),
  accessory("unicorn-rainbow-scarf", "unicorn", "צעיף הקשת", "scarf", 2, "neck", [
    path("back", [
      move(1, -4), cubic(-20, -4, -42, 3, -65, -7), quadratic(-52, 3, -63, 10),
      quadratic(-44, 9, -33, 17), quadratic(-47, 21, -58, 29),
      quadratic(-32, 28, -11, 17), line(8, 5), close(),
    ], PINK, { stroke: INK, strokeWidth: 2 }),
    detailPath("back", [move(-58, -2), quadratic(-40, 5, -18, 4), quadratic(-38, 11, -53, 14), quadratic(-34, 16, -16, 12)], GOLD, 3),
    detailPath("back", [move(-54, 5), quadratic(-37, 10, -20, 9), quadratic(-37, 16, -50, 20), quadratic(-31, 21, -12, 15)], MINT, 3),
    detailPath("back", [move(-48, 11), quadratic(-35, 16, -20, 14), quadratic(-32, 21, -42, 25), quadratic(-26, 25, -9, 18)], SKY, 3),
    path("front", [move(-6, -9), cubic(8, -15, 29, -11, 36, 1), quadratic(31, 14, 16, 17), quadratic(0, 14, -8, 4), close()], LAVENDER, { stroke: INK, strokeWidth: 2 }),
    detailPath("front", [move(-2, -4), quadratic(14, -9, 31, 1)], PINK, 3.2),
    detailPath("front", [move(0, 2), quadratic(15, -2, 29, 5)], GOLD, 3.2),
    detailPath("front", [move(2, 8), quadratic(16, 5, 25, 9)], MINT, 3.2),
    path("front", [move(26, -2), quadratic(32, -7, 38, 0), quadratic(42, 7, 34, 11), quadratic(25, 9, 26, -2), close()], WHITE, { stroke: INK, strokeWidth: 1.3 }),
    circle("front", 34, 2, 3, SKY),
  ]),
  accessory("unicorn-heart-glasses", "unicorn", "משקפי הלבבות", "glasses", 3, "eyes", [
    detailPath("front", [move(-29, -4), quadratic(-25, -9, -20, -8)], MAGENTA, 2.5),
    path("front", [
      move(-13, 10), cubic(-17, 5, -25, -1, -24, -7), cubic(-23, -15, -13, -16, -10, -9),
      cubic(-5, -16, 4, -13, 3, -5), cubic(2, 1, -5, 7, -13, 10), close(),
    ], PINK, { stroke: INK, strokeWidth: 1.8 }),
    path("front", [
      move(-13, 6), cubic(-17, 2, -21, -2, -20, -7), cubic(-19, -11, -13, -11, -10, -5),
      cubic(-6, -11, -1, -9, -1, -5), cubic(-1, -1, -6, 4, -13, 6), close(),
    ], LAVENDER, { opacity: 0.72 }),
    path("front", [
      move(15, 10), cubic(10, 5, 3, -1, 4, -7), cubic(5, -15, 14, -15, 18, -8),
      cubic(23, -14, 30, -11, 29, -4), cubic(28, 2, 22, 7, 15, 10), close(),
    ], PINK, { stroke: INK, strokeWidth: 1.8 }),
    path("front", [
      move(15, 6), cubic(11, 2, 7, -2, 8, -7), cubic(9, -11, 14, -11, 18, -4),
      cubic(22, -10, 26, -8, 25, -4), cubic(25, 0, 21, 4, 15, 6), close(),
    ], SKY, { opacity: 0.72 }),
    detailPath("front", [move(2, -5), quadratic(4, -8, 7, -5)], GOLD, 2.2),
    ellipse("front", -15, -7, 2, 3.5, WHITE, { opacity: 0.78 }),
    ellipse("front", 13, -7, 2, 3.5, WHITE, { opacity: 0.78 }),
    circle("front", 25, -8, 1.5, WHITE, { opacity: 0.9 }),
  ]),
  accessory("unicorn-star-hat", "unicorn", "כובע הכוכבים", "hat", 4, "head", [
    path("back", [
      move(-31, 4), quadratic(-11, -2, -2, -29), quadratic(5, -21, 9, -30),
      quadratic(19, -19, 20, -5), quadratic(28, -1, 31, 5), close(),
    ], PURPLE_DARK, { stroke: INK, strokeWidth: 2.1 }),
    path("front", [
      move(-23, 1), quadratic(-9, -5, -2, -27), quadratic(5, -20, 9, -29),
      quadratic(18, -17, 17, -4), quadratic(23, -1, 25, 3), close(),
    ], PURPLE, { stroke: INK, strokeWidth: 1.5 }),
    path("front", [move(-12, -5), quadratic(-5, -18, 1, -20), quadratic(5, -12, 12, -9), quadratic(4, -9, -2, -2), close()], 0xa66be8, { opacity: 0.8 }),
    path("front", [
      move(-34, 3), quadratic(-7, -3, 33, 3), quadratic(37, 7, 29, 10),
      quadratic(-4, 8, -35, 10), quadratic(-40, 7, -34, 3), close(),
    ], PURPLE_DARK, { stroke: INK, strokeWidth: 2 }),
    path("front", [move(-26, 1), quadratic(-2, -4, 25, 1), line(27, 7), quadratic(0, 4, -28, 7), close()], MAGENTA, { stroke: INK, strokeWidth: 1.2 }),
    roundedRect("front", -4, -2, 14, 6, 2, GOLD, { stroke: INK, strokeWidth: 1 }),
    star("front", 4, -16, 5, 2.2, GOLD_LIGHT, { stroke: INK, strokeWidth: 0.7 }),
    star("front", 14, -7, 3, 1.3, SKY),
    star("front", -9, -8, 2.5, 1.1, PINK),
    circle("front", -1, -22, 1.2, WHITE),
  ]),
  accessory("unicorn-butterfly-wings", "unicorn", "כנפי הפרפר", "wings", 5, "wing", [
    path("back", [
      move(14, 5), cubic(2, -20, -16, -40, -37, -38), cubic(-48, -36, -46, -17, -32, -3),
      cubic(-48, -5, -51, 14, -39, 25), cubic(-28, 36, -8, 25, 14, 5), close(),
    ], PINK, { stroke: INK, strokeWidth: 2.1, opacity: 0.9 }),
    path("back", [
      move(8, 1), cubic(-3, -17, -17, -32, -32, -32), cubic(-39, -30, -36, -18, -24, -7),
      cubic(-37, -7, -41, 8, -32, 17), cubic(-22, 25, -6, 18, 8, 1), close(),
    ], SKY, { stroke: WHITE, strokeWidth: 1, opacity: 0.78 }),
    path("back", [move(-8, -7), cubic(-15, -24, -28, -30, -34, -25), cubic(-34, -15, -23, -9, -8, -7), close()], LAVENDER, { opacity: 0.68 }),
    path("back", [move(-6, 4), cubic(-21, 0, -34, 7, -34, 15), cubic(-27, 23, -14, 15, -6, 4), close()], MAGENTA, { opacity: 0.65 }),
    detailPath("back", [move(11, 3), quadratic(-13, -12, -37, -33)], WHITE, 1.2, 0.8),
    detailPath("back", [move(8, 4), quadratic(-17, 4, -38, 20)], WHITE, 1.2, 0.8),
    detailPath("back", [move(-11, -8), quadratic(-22, -14, -28, -24)], PURPLE_DARK, 1.1, 0.65),
    detailPath("back", [move(-12, 4), quadratic(-23, 8, -28, 16)], PURPLE_DARK, 1.1, 0.65),
    ellipse("front", 10, 4, 5, 11, GOLD, { stroke: INK, strokeWidth: 1.4 }),
    circle("front", 9, 0, 2, WHITE, { opacity: 0.75 }),
    star("back", -30, -15, 2.5, 1, WHITE),
    star("back", -23, 12, 2.2, 0.9, GOLD_LIGHT),
  ]),
  accessory("unicorn-cloud-cape", "unicorn", "גלימת העננים", "cape", 6, "neck", [
    path("back", [
      move(2, -8), cubic(-21, -9, -49, -3, -66, 10), cubic(-56, 25, -65, 40, -56, 50),
      quadratic(-45, 43, -34, 50), quadratic(-23, 41, -8, 46), quadratic(-1, 23, 14, 5), close(),
    ], BLUE, { stroke: INK, strokeWidth: 2.3 }),
    path("back", [
      move(-3, -3), cubic(-22, -4, -46, 2, -58, 12), quadratic(-48, 26, -55, 40),
      quadratic(-41, 34, -30, 44), quadratic(-19, 34, -8, 40), quadratic(-2, 18, 9, 4), close(),
    ], LAVENDER, { opacity: 0.92 }),
    path("back", [move(-11, 3), cubic(-27, 5, -40, 9, -48, 16), quadratic(-34, 18, -22, 31), quadratic(-12, 22, -2, 31), line(5, 8), close()], 0xb88cf2, { opacity: 0.75 }),
    star("back", -38, 20, 3, 1.3, WHITE),
    star("back", -21, 30, 2.2, 1, GOLD_LIGHT),
    star("back", -47, 34, 2.2, 1, PINK),
    circle("back", -57, 43, 11, WHITE, { stroke: INK, strokeWidth: 1.2 }),
    circle("back", -43, 45, 13, WHITE, { stroke: INK, strokeWidth: 1.2 }),
    circle("back", -27, 45, 12, WHITE, { stroke: INK, strokeWidth: 1.2 }),
    circle("back", -12, 43, 10, WHITE, { stroke: INK, strokeWidth: 1.2 }),
    circle("front", -2, -5, 9, WHITE, { stroke: INK, strokeWidth: 1.2 }),
    circle("front", 7, -5, 10, WHITE, { stroke: INK, strokeWidth: 1.2 }),
    circle("front", 16, -2, 8, WHITE, { stroke: INK, strokeWidth: 1.2 }),
    circle("front", 8, -1, 6, GOLD, { stroke: INK, strokeWidth: 1.1 }),
    star("front", 8, -1, 4, 1.8, SKY),
  ]),
  accessory("unicorn-moon-tiara", "unicorn", "נזר הירח", "tiara", 7, "head", [
    detailPath("back", [move(-25, 5), quadratic(-3, -9, 25, 5)], GOLD, 3),
    path("front", [
      move(-8, -12), cubic(-2, -26, 13, -26, 20, -14), cubic(12, -18, 3, -15, 2, -6),
      cubic(-2, -7, -6, -9, -8, -12), close(),
    ], GOLD_LIGHT, { stroke: INK, strokeWidth: 1.5 }),
    path("front", [move(1, -21), cubic(7, -25, 15, -22, 18, -16), cubic(11, -20, 5, -17, 3, -11), cubic(0, -13, -1, -17, 1, -21), close()], WHITE, { opacity: 0.72 }),
    polygon("front", [[-17, -4], [-10, -11], [-3, -4], [-10, 4]], SKY, { stroke: INK, strokeWidth: 1 }),
    polygon("front", [[13, -4], [20, -11], [27, -4], [20, 4]], PINK, { stroke: INK, strokeWidth: 1 }),
    circle("front", 5, 3, 4.2, LAVENDER, { stroke: INK, strokeWidth: 1 }),
    star("front", 5, 3, 2.6, 1.1, WHITE),
    star("front", -22, -11, 2.3, 1, GOLD_LIGHT),
    star("front", 25, -14, 2.5, 1.1, WHITE),
  ]),
  accessory("unicorn-rainbow-wings", "unicorn", "כנפי הקשת", "wings", 8, "wing", [
    path("back", [
      move(18, 8), cubic(5, -18, -17, -43, -40, -41), cubic(-47, -32, -35, -19, -25, -10),
      cubic(-43, -14, -47, -1, -32, 6), cubic(-49, 4, -47, 19, -30, 20),
      cubic(-42, 23, -33, 37, -18, 30), quadratic(-3, 22, 18, 8), close(),
    ], PURPLE, { stroke: INK, strokeWidth: 2.2 }),
    path("back", [move(12, 4), cubic(-2, -17, -20, -35, -37, -36), cubic(-33, -27, -22, -17, -13, -10), close()], SKY, { stroke: WHITE, strokeWidth: 0.9 }),
    path("back", [move(12, 5), cubic(-8, -10, -27, -18, -37, -13), cubic(-31, -4, -20, 1, -8, 6), close()], MINT, { stroke: WHITE, strokeWidth: 0.9 }),
    path("back", [move(13, 7), cubic(-9, 1, -31, 3, -39, 10), cubic(-30, 16, -18, 17, -5, 13), close()], GOLD, { stroke: WHITE, strokeWidth: 0.9 }),
    path("back", [move(12, 9), cubic(-8, 10, -28, 18, -31, 26), cubic(-20, 29, -9, 21, 1, 15), close()], PINK, { stroke: WHITE, strokeWidth: 0.9 }),
    detailPath("back", [move(15, 7), quadratic(-8, -9, -39, -38)], WHITE, 1.3, 0.75),
    detailPath("back", [move(14, 8), quadratic(-13, 2, -39, 10)], WHITE, 1.1, 0.72),
    detailPath("back", [move(13, 10), quadratic(-7, 16, -29, 27)], WHITE, 1.1, 0.72),
    ellipse("front", 13, 8, 5.5, 12, GOLD, { stroke: INK, strokeWidth: 1.5 }),
    circle("front", 12, 4, 2, WHITE, { opacity: 0.8 }),
    star("back", -28, -25, 2.6, 1.1, WHITE),
    star("back", -29, 12, 2.3, 1, WHITE),
  ]),
  accessory("unicorn-princess-cape", "unicorn", "גלימת הנסיכה", "cape", 9, "neck", [
    path("back", [
      move(2, -8), cubic(-22, -9, -52, -2, -69, 11), cubic(-59, 27, -67, 43, -57, 54),
      quadratic(-44, 46, -31, 53), quadratic(-18, 42, -4, 48), quadratic(2, 22, 15, 5), close(),
    ], 0x8c246f, { stroke: INK, strokeWidth: 2.5 }),
    path("back", [
      move(-2, -3), cubic(-24, -4, -49, 2, -61, 13), quadratic(-50, 27, -57, 43),
      quadratic(-43, 35, -32, 46), quadratic(-19, 35, -8, 42), quadratic(-2, 18, 10, 4), close(),
    ], MAGENTA),
    path("back", [move(-10, 1), cubic(-28, 4, -43, 8, -51, 15), quadratic(-37, 21, -42, 35), quadratic(-29, 28, -20, 39), quadratic(-11, 25, 0, 32), line(6, 8), close()], ROSE, { opacity: 0.58 }),
    detailPath("back", [move(-61, 49), quadratic(-48, 39, -37, 47), quadratic(-27, 36, -16, 44), quadratic(-10, 37, -6, 42)], GOLD, 2.3),
    detailPath("back", [move(-38, 5), quadratic(-27, 20, -28, 40)], GOLD_LIGHT, 1.2, 0.8),
    circle("front", -2, -5, 9, IVORY, { stroke: INK, strokeWidth: 1.2 }),
    circle("front", 7, -5, 10, IVORY, { stroke: INK, strokeWidth: 1.2 }),
    circle("front", 16, -2, 8, IVORY, { stroke: INK, strokeWidth: 1.2 }),
    path("front", [move(8, 7), cubic(1, 2, -1, -4, 4, -7), cubic(8, -10, 12, -7, 12, -2), cubic(15, -7, 22, -6, 23, 0), cubic(23, 5, 16, 9, 8, 13), close()], GOLD, { stroke: INK, strokeWidth: 1.3 }),
    path("front", [move(9, 8), cubic(4, 4, 3, -1, 6, -3), cubic(9, -5, 12, -2, 12, 1), cubic(15, -3, 19, -2, 19, 1), cubic(19, 4, 14, 7, 9, 8), close()], PINK),
    circle("front", 7, -1, 1.5, WHITE, { opacity: 0.75 }),
  ]),
  accessory("unicorn-magic-crown", "unicorn", "כתר קסם הקשת", "crown", 10, "head", [
    detailPath("back", [move(-28, 8), quadratic(-4, -12, 29, 8)], GOLD, 4),
    polygon("front", [[-25, 5], [-20, -14], [-10, -4], [-5, 8]], PINK, { stroke: INK, strokeWidth: 1.4 }),
    polygon("front", [[-16, 5], [-8, -25], [3, -8], [2, 9]], SKY, { stroke: INK, strokeWidth: 1.5 }),
    polygon("front", [[-3, 6], [5, -30], [15, -7], [10, 10]], LAVENDER, { stroke: INK, strokeWidth: 1.6 }),
    polygon("front", [[10, 7], [19, -23], [28, -3], [22, 10]], MINT, { stroke: INK, strokeWidth: 1.5 }),
    path("front", [move(-18, 1), line(-9, -18), line(-5, -4), close()], WHITE, { opacity: 0.58 }),
    path("front", [move(-1, 0), line(5, -23), line(10, -6), close()], WHITE, { opacity: 0.62 }),
    path("front", [move(14, 1), line(19, -16), line(24, -3), close()], WHITE, { opacity: 0.6 }),
    roundedRect("front", -27, 5, 55, 10, 4, GOLD, { stroke: INK, strokeWidth: 1.7 }),
    roundedRect("front", -21, 6.5, 43, 3.5, 1.7, GOLD_LIGHT, { opacity: 0.86 }),
    polygon("front", [[1, 1], [9, 8], [1, 16], [-7, 8]], WHITE, { stroke: MAGENTA, strokeWidth: 1.4 }),
    polygon("front", [[1, 4], [5, 8], [1, 12], [-3, 8]], SKY),
    circle("front", -16, 9.5, 3.2, PINK, { stroke: INK, strokeWidth: 0.8 }),
    circle("front", 18, 9.5, 3.2, LAVENDER, { stroke: INK, strokeWidth: 0.8 }),
    star("front", -27, -12, 3.2, 1.3, WHITE),
    star("front", 28, -14, 3.4, 1.4, GOLD_LIGHT),
  ]),
] as const;

export interface WardrobeSkinAsset {
  readonly id: AccessoryId;
  readonly key: string;
  readonly href: string;
}

/** Minimal preload manifest consumed by the Phaser scene. */
export const WARDROBE_SKIN_ASSETS: readonly WardrobeSkinAsset[] = WARDROBE_ACCESSORIES.map(
  ({ id, outfitTextureKey: key, outfitAssetHref: href }) => ({ id, key, href }),
);

const ACCESSORY_ID_SET: ReadonlySet<string> = new Set(ACCESSORY_IDS);
const ACCESSORY_BY_ID: ReadonlyMap<AccessoryId, AccessoryDefinition> = new Map(
  WARDROBE_ACCESSORIES.map((item) => [item.id, item]),
);

const ACCESSORIES_BY_CHARACTER: Readonly<Record<CharacterId, readonly AccessoryDefinition[]>> = {
  dragon: WARDROBE_ACCESSORIES.filter((item) => item.characterId === "dragon"),
  unicorn: WARDROBE_ACCESSORIES.filter((item) => item.characterId === "unicorn"),
};

export function isAccessoryId(value: unknown): value is AccessoryId {
  return typeof value === "string" && ACCESSORY_ID_SET.has(value);
}

export function getAccessoryDefinition(id: AccessoryId): AccessoryDefinition {
  const definition = ACCESSORY_BY_ID.get(id);
  if (!definition) throw new Error(`Unknown accessory: ${id}`);
  return definition;
}

export function findAccessoryDefinition(value: unknown): AccessoryDefinition | null {
  return isAccessoryId(value) ? ACCESSORY_BY_ID.get(value) ?? null : null;
}

export function getAccessoriesForCharacter(characterId: CharacterId): readonly AccessoryDefinition[] {
  return ACCESSORIES_BY_CHARACTER[characterId];
}

export function getAccessoryForCharacter(
  characterId: CharacterId,
  value: unknown,
): AccessoryDefinition | null {
  const definition = findAccessoryDefinition(value);
  return definition?.characterId === characterId ? definition : null;
}

export function getAccessoryReward(
  characterId: CharacterId,
  level: number,
): AccessoryDefinition | null {
  return ACCESSORIES_BY_CHARACTER[characterId].find((item) => item.unlockAfterLevel === level) ?? null;
}

export function completedLevelId(characterId: CharacterId, level: number): string {
  return `${characterId}-${level}`;
}

export function getUnlockedAccessories(
  characterId: CharacterId,
  completedLevelIds: readonly string[],
): readonly AccessoryDefinition[] {
  const completed = new Set(completedLevelIds);
  return ACCESSORIES_BY_CHARACTER[characterId].filter((item) =>
    completed.has(completedLevelId(characterId, item.unlockAfterLevel)),
  );
}

export function getUnlockedAccessoryIds(
  characterId: CharacterId,
  completedLevelIds: readonly string[],
): readonly AccessoryId[] {
  return getUnlockedAccessories(characterId, completedLevelIds).map((item) => item.id);
}

export function isAccessoryUnlocked(
  characterId: CharacterId,
  accessoryId: unknown,
  completedLevelIds: readonly string[],
): accessoryId is AccessoryId {
  const definition = getAccessoryForCharacter(characterId, accessoryId);
  return Boolean(
    definition && completedLevelIds.includes(completedLevelId(characterId, definition.unlockAfterLevel)),
  );
}

/**
 * Returns a safe persisted choice. `null` is the always-available no-accessory option.
 * Unknown, cross-character, and locked values all degrade to `null`.
 */
export function validateEquippedAccessoryId(
  characterId: CharacterId,
  value: unknown,
  completedLevelIds: readonly string[],
): AccessoryId | null {
  if (value === null || value === undefined) return null;
  return isAccessoryUnlocked(characterId, value, completedLevelIds) ? value : null;
}

export function colorToCss(color: number): string {
  return `#${Math.max(0, Math.min(0xffffff, color)).toString(16).padStart(6, "0")}`;
}
